"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TOAST_MS = void 0;
exports.Toaster = Toaster;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const cn_1 = require("../lib/cn");
const motion_1 = require("../lib/motion");
const button_1 = require("./button");
const icon_1 = require("./icon");
/**
 * ⚠️ **`text-status-*`, not `text-emerald-600`.** The design system's own implementation reaches
 * for a raw Tailwind palette colour here, in a system whose first rule is that colours live in
 * tokens — because `--status-ok` was never bridged to a utility. It is now (`base.css`), so this
 * uses the token and inherits every theme.
 *
 * ⚠️ **Colour is never the only channel** (`state-rules.md` §10): the glyph differs per variant
 * as well as the hue, so an error and a success are distinguishable in monochrome and to a
 * colour-blind reader.
 */
const VARIANTS = {
    success: { icon: 'circle-check', className: 'text-status-ok' },
    error: { icon: 'triangle-alert', className: 'text-destructive' },
    info: { icon: 'circle-dot', className: 'text-status-info' },
};
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Bottom-right toast stack, and the app's only undo affordance.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Undo is a toast action, not a separate control** (`state-rules.md` §7) — the mockup's
 * pattern, and it belongs on nearly every mutation. That makes this component load-bearing
 * rather than cosmetic: it is where "the delete you just did is reversible" lives.
 *
 * ⚠️ **Controlled list, owned timer.** The list comes from the caller and so does removal — but
 * the auto-dismiss timer is HERE, which reverses what this note used to say. Leaving it to
 * callers meant twenty-two of them and one timer between them (the Control Panel's): every
 * toast in the product stayed up until somebody closed it. The timer only ever calls
 * `onDismiss`, so the caller still owns the array and there is still one source of truth for it.
 *
 * ⚠️⚠️ **THE CLOCK STOPS WHILE SOMEBODY IS LOOKING.** Hover or focus anywhere in the stack pauses
 * every toast in it, and so does a hidden tab — a confirmation that expires while its reader is
 * in another window was never read. An undo stays longer than a plain confirmation, because it
 * asks for a decision, and one whose undo is in flight does not expire under the spinner.
 *
 * ⚠️ **`role="status"` with `aria-live="polite"`, per toast.** `assertive` interrupts whatever
 * the reader is saying, which for a confirmation of something the user just did is rude and
 * loses their place. A rejection they need to act on is an inline error, not a toast.
 */
/** How long a toast stays, in ms: longer when it offers an undo. */
exports.TOAST_MS = { plain: 5000, action: 8000 };
const lifetime = (toast) => toast.action === undefined ? exports.TOAST_MS.plain : exports.TOAST_MS.action;
function Toaster({ toasts, onDismiss, label, dismissLabel }) {
    const [held, setHeld] = (0, react_1.useState)(false);
    const [hidden, setHidden] = (0, react_1.useState)(() => typeof document !== 'undefined' && document.visibilityState === 'hidden');
    /* Time left per toast, carried across pauses. */
    const remaining = (0, react_1.useRef)(new Map());
    /**
     * ⚠️⚠️ **A LEAVING TOAST IS KEPT HERE, NOT IN THE CALLER.** The caller drops it from its array
     * the moment it is dismissed — which is right, it is gone — and this keeps a copy on screen for
     * the 200ms it takes to fade, at the position it left from, `inert` so nothing in it can be
     * pressed twice. Without an animation engine (jsdom) or under reduced motion it is not kept.
     */
    const [leaving, setLeaving] = (0, react_1.useState)([]);
    const [previous, setPrevious] = (0, react_1.useState)(toasts);
    if (previous !== toasts) {
        setPrevious(toasts);
        const present = new Set(toasts.map((toast) => toast.id));
        const gone = previous
            .map((toast, at) => ({ toast, at }))
            .filter(({ toast }) => !present.has(toast.id));
        if (gone.length > 0 && (0, motion_1.motionAllowed)()) {
            setLeaving((current) => [...current.filter(({ toast }) => !present.has(toast.id)), ...gone]);
        }
    }
    (0, react_1.useEffect)(() => {
        if (leaving.length === 0)
            return;
        const timer = window.setTimeout(() => {
            setLeaving([]);
        }, motion_1.EXIT_MS.toast);
        return () => {
            window.clearTimeout(timer);
        };
    }, [leaving]);
    (0, react_1.useEffect)(() => {
        const onVisibility = () => {
            setHidden(document.visibilityState === 'hidden');
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);
    /* Per-toast, keyed by id: an undo in flight, and an undo that failed. Held here rather than
     * in the parent because it is presentation state about a button, and lifting it would put a
     * spinner flag in the caller's domain model. */
    const [pending, setPending] = (0, react_1.useState)(new Set());
    const [failed, setFailed] = (0, react_1.useState)(new Set());
    const mark = (set, id, on) => {
        set((previous) => {
            const next = new Set(previous);
            if (on)
                next.add(id);
            else
                next.delete(id);
            return next;
        });
    };
    const runAction = async (toast) => {
        if (toast.action === undefined)
            return;
        mark(setFailed, toast.id, false);
        mark(setPending, toast.id, true);
        try {
            await toast.action.onAction();
            /* Success dismisses the toast: the thing it was offering to reverse is reversed, so the
             * offer is stale. Leaving it up invites a second undo of an already-undone change. */
            onDismiss?.(toast.id);
        }
        catch {
            /* ⚠️ Swallowed deliberately and reported in the UI. Rethrowing here would surface as an
             * unhandled rejection in a component the user cannot see, while the button they pressed
             * sat there looking like it worked. */
            mark(setFailed, toast.id, true);
        }
        finally {
            mark(setPending, toast.id, false);
        }
    };
    /**
     * ⚠️ **Re-armed when the SET of toasts changes, not when the array does.** Callers hand over a
     * fresh array every render (`[...toasts]`), so keying on the array would restart the clock on
     * every keystroke anywhere on the screen and nothing would ever expire. The key is the ids —
     * the toasts still running, so one whose undo is in flight drops out and stops its clock.
     */
    const live = toasts.filter((toast) => !pending.has(toast.id));
    const key = live.map((toast) => `${String(toast.id)}:${String(lifetime(toast))}`).join('|');
    const running = onDismiss !== undefined && !held && !hidden && key !== '';
    /* Read through a ref so the effect below re-arms on `key`, not on a fresh array or arrow. */
    const latest = (0, react_1.useRef)({ live, onDismiss });
    latest.current = { live, onDismiss };
    (0, react_1.useEffect)(() => {
        if (!running)
            return;
        const left = remaining.current;
        const armed = latest.current.live;
        for (const id of [...left.keys()]) {
            if (!armed.some((toast) => toast.id === id))
                left.delete(id);
        }
        const started = Date.now();
        const timers = armed.map((toast) => window.setTimeout(() => {
            latest.current.onDismiss?.(toast.id);
        }, left.get(toast.id) ?? lifetime(toast)));
        return () => {
            const elapsed = Date.now() - started;
            for (const toast of armed) {
                left.set(toast.id, Math.max(0, (left.get(toast.id) ?? lifetime(toast)) - elapsed));
            }
            timers.forEach((timer) => {
                window.clearTimeout(timer);
            });
        };
    }, [running, key]);
    /* The live toasts, with the leaving ones put back where they were. */
    const shown = toasts.map((toast) => ({
        toast,
        isLeaving: false,
    }));
    for (const gone of [...leaving].sort((a, b) => a.at - b.at)) {
        if (toasts.some((toast) => toast.id === gone.toast.id))
            continue;
        shown.splice(Math.min(gone.at, shown.length), 0, { toast: gone.toast, isLeaving: true });
    }
    return ((0, jsx_runtime_1.jsx)("div", { role: "region", "aria-label": label, 
        /* `flex-col-reverse` so the newest is nearest the corner and older ones ride up, which is
         * what makes a stack readable when three arrive at once. The gap is each slot's own top
         * padding, so a leaving toast can collapse its share of it — see `.toast-slot`. */
        className: "fixed right-4 bottom-4 z-[70] flex w-[356px] flex-col-reverse", onPointerEnter: () => {
            setHeld(true);
        }, onPointerLeave: () => {
            setHeld(false);
        }, onFocus: () => {
            setHeld(true);
        }, onBlur: (event) => {
            if (!event.currentTarget.contains(event.relatedTarget))
                setHeld(false);
        }, children: shown.map(({ toast, isLeaving }) => {
            const variant = VARIANTS[toast.variant ?? 'info'];
            const busy = pending.has(toast.id);
            const broke = failed.has(toast.id);
            return ((0, jsx_runtime_1.jsx)("div", { className: "toast-slot", "data-leaving": isLeaving ? '' : undefined, inert: isLeaving, children: (0, jsx_runtime_1.jsxs)("div", { role: "status", "aria-live": "polite", className: "toast-in bg-popover text-popover-foreground shadow-pop flex items-start gap-2.5 rounded-lg border p-3", children: [(0, jsx_runtime_1.jsx)(icon_1.Icon, { name: variant.icon, size: 15, className: (0, cn_1.cn)('mt-px', variant.className) }), (0, jsx_runtime_1.jsxs)("div", { className: "min-w-0 flex-1", children: [(0, jsx_runtime_1.jsx)("div", { className: "text-[13px] font-medium", children: toast.title }), toast.description === undefined ? null : ((0, jsx_runtime_1.jsx)("div", { className: "text-muted-foreground mt-0.5 text-[12px] leading-snug", children: toast.description })), toast.action === undefined ? null : ((0, jsx_runtime_1.jsx)("div", { className: "mt-2", children: broke ? (
                                    /* ⚠️ The rule's actual requirement: a failed undo SAYS SO. It replaces the
                                     * button rather than sitting beside it, because offering the same action
                                     * again after it failed for a reason the user cannot see is a loop. */
                                    (0, jsx_runtime_1.jsx)("p", { className: "text-destructive text-[12px] leading-snug", children: toast.action.failureLabel })) : ((0, jsx_runtime_1.jsx)(button_1.Button, { variant: "outline", size: "sm", disabled: busy, onClick: () => void runAction(toast), children: toast.action.label })) }))] }), (0, jsx_runtime_1.jsx)("button", { type: "button", className: "text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 rounded-sm outline-none focus-visible:ring-[3px]", onClick: () => onDismiss?.(toast.id), "aria-label": dismissLabel, children: (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "x", size: 13 }) })] }) }, toast.id));
        }) }));
}
//# sourceMappingURL=toaster.js.map